import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { fetchPlatformChatSession, fetchPlatformChatSessions } from "../api/analyticsClient";
import { fetchWorkspace, getAdminUser } from "../api/adminClient";
import { applyServerWorkspaceIdentity, clearServerWorkspaceIdentity } from "./workspaceContext";
import { mapApiMessagesToThread } from "./components/NexusConversationThread";

const NexusContext = createContext(null);

export function NexusProvider({ children }) {
  const location = useLocation();
  const [dockOpen, setDockOpen] = useState(false);
  const [pageContext, setPageContext] = useState(null);
  /** In-memory only — fresh empty chat on every full page reload. */
  const [platformSessionId, setPlatformSessionId] = useState("");
  const [threadMessages, setThreadMessages] = useState([]);
  const [pendingUserMessage, setPendingUserMessage] = useState("");
  const [conversationList, setConversationList] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [loadingConversationId, setLoadingConversationId] = useState("");
  const [conversationsRefreshKey, setConversationsRefreshKey] = useState(0);
  const [pendingPrompt, setPendingPrompt] = useState("");
  const [pendingWorkflowRequest, setPendingWorkflowRequest] = useState("");
  const commandFocusRef = useRef(null);
  const workflowStartRef = useRef(null);
  const loadedSessionRef = useRef("");

  const refreshConversationList = useCallback(async () => {
    setConversationsLoading(true);
    try {
      const res = await fetchPlatformChatSessions();
      setConversationList(Array.isArray(res?.data?.sessions) ? res.data.sessions : []);
    } catch {
      setConversationList([]);
    } finally {
      setConversationsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshConversationList();
  }, [refreshConversationList, conversationsRefreshKey]);

  useEffect(() => {
    const user = getAdminUser();
    if (!user?.tenant_id || user.is_platform_admin) {
      clearServerWorkspaceIdentity();
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWorkspace();
        if (cancelled) return;
        const ws = res.data?.workspace;
        const onboarding = res.data?.onboarding;
        const designationLabels = Array.isArray(ws?.designations)
          ? ws.designations.map((d) => (typeof d === "string" ? d : d?.label)).filter(Boolean)
          : [];
        applyServerWorkspaceIdentity({
          tenant_id: String(user.tenant_id),
          tenant_name: ws?.name || user.tenant_name || null,
          product_display_name: ws?.product_display_name || user.product_display_name,
          email_pack: ws?.email_pack || user.email_pack || "generic",
          is_classic: false,
          designations: designationLabels,
          onboarding: onboarding || undefined,
        });
      } catch {
        if (!cancelled) clearServerWorkspaceIdentity();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const bumpConversations = useCallback(() => {
    setConversationsRefreshKey((k) => k + 1);
  }, []);

  const startNewChat = useCallback(() => {
    setPlatformSessionId("");
    setThreadMessages([]);
    setPendingUserMessage("");
    loadedSessionRef.current = "";
  }, []);

  const routePathRef = useRef(null);
  useEffect(() => {
    const path = location.pathname;
    if (routePathRef.current !== null && routePathRef.current !== path) {
      startNewChat();
      setPendingWorkflowRequest("");
      setPendingPrompt("");
    }
    routePathRef.current = path;
  }, [location.pathname, startNewChat]);

  const openConversation = useCallback(
    async (sessionId) => {
      const sid = (sessionId || "").trim();
      if (!sid) return { ok: false, messages: [] };
      setLoadingConversationId(sid);
      try {
        const res = await fetchPlatformChatSession(sid);
        const msgs = mapApiMessagesToThread(res?.data?.messages || []);
        setPlatformSessionId(sid);
        setThreadMessages(msgs);
        setPendingUserMessage("");
        loadedSessionRef.current = sid;
        return { ok: true, messages: msgs };
      } catch {
        return { ok: false, messages: [] };
      } finally {
        setLoadingConversationId("");
      }
    },
    []
  );

  const closeDock = useCallback(() => {
    setDockOpen(false);
    setPendingPrompt("");
  }, []);

  const openDock = useCallback((prompt = "") => {
    if (prompt) setPendingPrompt(String(prompt));
    setDockOpen(true);
  }, []);

  const toggleDock = useCallback(() => {
    setDockOpen((open) => !open);
  }, []);

  const registerCommandFocus = useCallback((focusFn) => {
    commandFocusRef.current = focusFn;
    return () => {
      if (commandFocusRef.current === focusFn) {
        commandFocusRef.current = null;
      }
    };
  }, []);

  const focusCommand = useCallback(() => {
    if (typeof commandFocusRef.current === "function") {
      commandFocusRef.current();
      return;
    }
    setDockOpen(true);
  }, []);

  const registerWorkflowStart = useCallback((startFn) => {
    workflowStartRef.current = startFn;
    return () => {
      if (workflowStartRef.current === startFn) {
        workflowStartRef.current = null;
      }
    };
  }, []);

  const startWorkflow = useCallback((guideId) => {
    const id = String(guideId || "").trim();
    if (!id) return false;
    if (typeof workflowStartRef.current === "function") {
      workflowStartRef.current(id);
      return true;
    }
    setPendingWorkflowRequest(id);
    setDockOpen(true);
    return false;
  }, []);

  const clearPendingWorkflowRequest = useCallback(() => {
    setPendingWorkflowRequest("");
  }, []);

  const value = useMemo(
    () => ({
      dockOpen,
      setDockOpen,
      openDock,
      closeDock,
      toggleDock,
      pageContext,
      setPageContext,
      pendingPrompt,
      setPendingPrompt,
      pendingWorkflowRequest,
      clearPendingWorkflowRequest,
      startWorkflow,
      registerWorkflowStart,
      focusCommand,
      registerCommandFocus,
      platformSessionId,
      setPlatformSessionId,
      threadMessages,
      setThreadMessages,
      pendingUserMessage,
      setPendingUserMessage,
      conversationList,
      conversationsLoading,
      loadingConversationId,
      refreshConversationList,
      startNewChat,
      openConversation,
      bumpConversations,
      conversationsRefreshKey,
      clearPlatformSession: startNewChat,
      dockSessionConversationId: platformSessionId,
      setDockSessionConversationId: setPlatformSessionId,
      activeConversationId: platformSessionId,
      setActiveConversationId: setPlatformSessionId,
    }),
    [
      dockOpen,
      pageContext,
      openDock,
      closeDock,
      toggleDock,
      focusCommand,
      registerCommandFocus,
      platformSessionId,
      threadMessages,
      pendingUserMessage,
      conversationList,
      conversationsLoading,
      loadingConversationId,
      refreshConversationList,
      startNewChat,
      openConversation,
      bumpConversations,
      conversationsRefreshKey,
      pendingPrompt,
      pendingWorkflowRequest,
      clearPendingWorkflowRequest,
      startWorkflow,
      registerWorkflowStart,
    ]
  );

  return <NexusContext.Provider value={value}>{children}</NexusContext.Provider>;
}

export function useNexus() {
  const ctx = useContext(NexusContext);
  if (!ctx) {
    throw new Error("useNexus must be used within NexusProvider");
  }
  return ctx;
}

export function useNexusOptional() {
  return useContext(NexusContext);
}
